import React from "react";
import {
    faChevronLeft,
    faChevronRight,
} from "@fortawesome/pro-regular-svg-icons";
import { ResolvedBreadcrumb } from "@plugins/gatsby-source-paligo/src/types";
import { cx } from "class-variance-authority";
import { useNavigationPane } from "contexts/NavigationContext";
import { usePageHistoryContext } from "contexts/PageHistoryContext";
import { useProductVersion } from "contexts/ProductVersionContext";

import { Breadcrumbs } from "components/Breadcrumbs/Breadcrumbs";
import { LinkButton } from "components/Button/LinkButton";
import { FaIcon } from "components/FaIcon/FaIcon";
import { DocumentNavMenu } from "components/layout/DocumentNavMenu/DocumentNavMenu";
import { Footer } from "components/layout/Footer/Footer";
import { WasThisHelpful } from "components/layout/WasThisHelpful/WasThisHelpful";

import { DocumentNodeType } from "types/types";

import "./DocumentLayout.scss";

type DocumentLayoutProps = {
    documentNode?: DocumentNodeType;
    breadcrumbs?: Array<ResolvedBreadcrumb>;
    rightSidebar?: React.ReactNode | Array<React.ReactNode>;
    className?: string;
};

export function DocumentLayout({
    children,
    documentNode,
    breadcrumbs = [],
    rightSidebar,
    className = "",
}: React.PropsWithChildren<DocumentLayoutProps>) {
    const { setNavigationPanePosition, navigationPanePosition } =
        useNavigationPane();

    const { dispatch: pageHistoryDispatch } = usePageHistoryContext();
    const {
        productVersionNode: {
            name: version,
            productNode: { name: product },
        },
    } = useProductVersion();

    const sidebarContainerRef = React.useRef<Nullable<HTMLDivElement>>(null);

    let breadcrumbElement;

    if (breadcrumbs?.length) {
        breadcrumbElement = <Breadcrumbs breadcrumbs={breadcrumbs} />;
    }

    const beforeNavigate = () => {
        if (sidebarContainerRef?.current) {
            setNavigationPanePosition(sidebarContainerRef.current.scrollTop);
        }
    };

    React.useEffect(() => {
        // store navigation tree scroll state
        if (sidebarContainerRef?.current) {
            sidebarContainerRef.current.scrollTop = navigationPanePosition;
        }
        // store navigation state (to keep a page history)
        if (documentNode?.path) {
            pageHistoryDispatch({
                type: "push_history",
                value: {
                    path: documentNode.path,
                    title: documentNode.name,
                    // removes the breadcrumb of the document itself
                    breadcrumbs: breadcrumbs.slice(0, -1),
                    product: product,
                    version: version,
                    accessedAt: new Date(),
                },
            });
        }
    }, [
        breadcrumbs,
        pageHistoryDispatch,
        documentNode,
        navigationPanePosition,
        product,
        version,
    ]);

    const classes = cx("document-page-container", className);

    let nextPrevNavigation;

    if (documentNode) {
        let prevDocumentLink = <div className="document-sibling" />;
        let nextDocumentLink = <div className="document-sibling" />;

        if (documentNode.previousDocumentNode) {
            prevDocumentLink = (
                <div className="document-sibling prev">
                    <LinkButton
                        className="document-sibling-link prev"
                        iconLeft={
                            <FaIcon
                                icon={faChevronLeft}
                                mr="0-5x"
                                height="2-5x"
                            />
                        }
                        to={documentNode.previousDocumentNode?.path}
                        variation="ghost-primary"
                        size="md"
                    >
                        {documentNode.previousDocumentNode?.name}
                    </LinkButton>
                </div>
            );
        }

        if (documentNode.nextDocumentNode) {
            nextDocumentLink = (
                <div className="document-sibling next">
                    <LinkButton
                        className="document-sibling-link next"
                        iconRight={
                            <FaIcon
                                icon={faChevronRight}
                                ml="0-5x"
                                height="2-5x"
                            />
                        }
                        to={documentNode.nextDocumentNode?.path}
                        variation="ghost-primary"
                        size="md"
                    >
                        {documentNode.nextDocumentNode?.name}
                    </LinkButton>
                </div>
            );
        }

        nextPrevNavigation = (
            <div className="document-sibling-navigation">
                {prevDocumentLink}
                {nextDocumentLink}
            </div>
        );
    }

    return (
        <div className={classes}>
            <div ref={sidebarContainerRef} className="sidebar-container">
                <DocumentNavMenu beforeNavigate={beforeNavigate} />
            </div>

            <div className="document-body-container">
                {breadcrumbElement && (
                    <div className="document-top-bar">{breadcrumbElement}</div>
                )}

                <article>{children}</article>

                <WasThisHelpful />

                {nextPrevNavigation}

                <Footer />
            </div>

            <div className="right-sidebar-container">
                <div className="right-sidebar-wrapper">{rightSidebar}</div>
            </div>
        </div>
    );
}
